import * as PIXI from 'pixi.js';
import type { Token, HpTier } from '@machina/core';
import { TOKEN_SIZE_SQUARES } from '@machina/core';
import { setPixiTokenClicked } from './pixiTokenState';
import { useTokenImageStore } from '../stores/tokenImageStore';

/**
 * HP Tier colors for border effects
 */
const HP_TIER_COLORS: Record<HpTier, number> = {
  healthy: 0x4caf50,     // Green
  wounded: 0xffc107,     // Amber/Yellow
  bloody: 0xff5722,      // Deep Orange
  critical: 0xf44336,    // Red
  unconscious: 0x607d8b, // Blue-gray
  dead: 0x424242,        // Dark gray
};

/** Condition labels and SVG paths (same paths as Icon.tsx for visual parity) */
const CONDITION_ICONS: Record<string, { label: string; path: string }> = {
  blinded:        { label: 'Blinded',        path: 'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z' },
  charmed:        { label: 'Charmed',        path: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' },
  deafened:       { label: 'Deafened',       path: 'M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z' },
  frightened:     { label: 'Frightened',     path: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' },
  grappled:       { label: 'Grappled',       path: 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z' },
  incapacitated:  { label: 'Incapacitated',  path: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z' },
  invisible:      { label: 'Invisible',      path: 'M12 2c-5.52 0-10 4.48-10 10v10l3-3 3 3 3-3 3 3 3-3 3 3V12c0-5.52-4.48-10-10-10zm-1 14H9v-2h2v2zm0-4H9V8h2v4zm4 4h-2v-2h2v2zm0-4h-2V8h2v4z' },
  paralyzed:      { label: 'Paralyzed',      path: 'M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22v-2z' },
  petrified:      { label: 'Petrified',      path: 'M12 2L2 12l10 10 10-10L12 2zm0 2.83L19.17 12 12 19.17 4.83 12 12 4.83z' },
  poisoned:       { label: 'Poisoned',       path: 'M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z' },
  prone:          { label: 'Prone',          path: 'M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z' },
  restrained:     { label: 'Restrained',     path: 'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z' },
  stunned:        { label: 'Stunned',        path: 'M7 2v11h3v9l7-12h-4l4-8z' },
  unconscious:    { label: 'Unconscious',    path: 'M9 2c-1.05 0-2.05.16-3 .46 4.06 1.27 7 5.06 7 9.54 0 4.48-2.94 8.27-7 9.54.95.3 1.95.46 3 .46 5.52 0 10-4.48 10-10S14.52 2 9 2z' },
  concentrating:  { label: 'Concentrating',  path: 'M12 2L2 12l10 10 10-10L12 2zm0 2.83L19.17 12 12 19.17 4.83 12 12 4.83z' },
  bloodied:       { label: 'Bloodied',       path: 'M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z' },
};

/**
 * Pre-rendered condition icon textures.
 * Uses browser's native Path2D renderer for crisp icons at small sizes,
 * matching the quality of the React Icon component in flyout panels.
 */
const conditionIconTextureCache = new Map<string, PIXI.Texture>();
const ICON_RENDER_SIZE = 32; // 2x resolution for crisp rendering at 14-16px display

function getConditionIconTexture(svgPath: string): PIXI.Texture {
  const cached = conditionIconTextureCache.get(svgPath);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = ICON_RENDER_SIZE;
  canvas.height = ICON_RENDER_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return PIXI.Texture.WHITE;

  // Scale from 24x24 viewBox to our render size
  const scale = ICON_RENDER_SIZE / 24;
  ctx.scale(scale, scale);
  ctx.fillStyle = '#ffffff';
  ctx.fill(new Path2D(svgPath));

  const texture = PIXI.Texture.from(canvas);
  conditionIconTextureCache.set(svgPath, texture);
  return texture;
}

/**
 * TokenRenderer
 *
 * Handles Pixi.js rendering for a single token.
 * Creates graphics for shape, HP bar, conditions, selection ring.
 * Supports token images via imageUrl with fallback to colored shapes.
 */
export class TokenRenderer {
  public container: PIXI.Container;
  public readonly tokenId: string;

  private shape: PIXI.Graphics;
  /**
   * When true, the full-name tooltip stays visible on every token (GM mode).
   * Set from React via setAlwaysShowNames(); all existing renderers update in place.
   */
  static alwaysShowNames = false;
  private static instances = new Set<TokenRenderer>();

  static setAlwaysShowNames(on: boolean): void {
    TokenRenderer.alwaysShowNames = on;
    for (const r of TokenRenderer.instances) {
      if (!r.isDragging) r.tooltip.visible = on;
    }
  }

  private imageSprite: PIXI.Sprite | null = null;
  private imageMask: PIXI.Graphics | null = null;
  private currentImageUrl: string | null = null;
  private hpBar: PIXI.Graphics;
  private selectionRing: PIXI.Graphics;
  private label: PIXI.Text;
  private conditionIcons: PIXI.Container;
  private deadOverlay: PIXI.Graphics;
  private tooltip: PIXI.Container;
  private tooltipBg: PIXI.Graphics;
  private tooltipText: PIXI.Text;

  private echoGlow: PIXI.Graphics;

  private gridSize: number;
  private data: Token;
  // Dirty tracking — avoid full redraws when only specific parts change
  private prevHpTier: HpTier | null = null;
  private prevConditionKey = '';
  private isDragging = false;
  private hasMoved = false;  // Track if actual movement occurred
  private dragOffset = { x: 0, y: 0 };
  private dragStart = { x: 0, y: 0 };
  private echoPhase = 0;
  private echoTickerFn: (() => void) | null = null;

  // Event callbacks
  public onDragStart?: (tokenId: string) => void;
  public onDragMove?: (tokenId: string, x: number, y: number) => void;
  public onDragEnd?: (tokenId: string, x: number, y: number) => void;
  public onClick?: (tokenId: string, shiftKey: boolean) => void;

  constructor(token: Token, gridSize: number) {
    this.tokenId = token.id;
    this.data = token;
    this.gridSize = gridSize;

    // Create container for all token graphics
    this.container = new PIXI.Container();
    this.container.eventMode = 'static';
    this.container.cursor = token.locked ? 'not-allowed' : 'pointer';

    // Create graphics layers (order matters for z-index)
    this.selectionRing = new PIXI.Graphics();
    this.echoGlow = new PIXI.Graphics();
    this.shape = new PIXI.Graphics();
    this.hpBar = new PIXI.Graphics();
    this.conditionIcons = new PIXI.Container();
    this.deadOverlay = new PIXI.Graphics();
    this.label = new PIXI.Text('', {
      fontFamily: 'sans-serif',
      fontSize: 12,
      fill: 0xffffff,
      align: 'center',
    });

    // Create tooltip (name flyout on hover)
    this.tooltip = new PIXI.Container();
    this.tooltip.visible = TokenRenderer.alwaysShowNames;
    TokenRenderer.instances.add(this);
    this.tooltipBg = new PIXI.Graphics();
    this.tooltipText = new PIXI.Text('', {
      fontFamily: 'var(--font-display), Georgia, serif',
      fontSize: 13,
      fill: 0xf4e4bc, // Parchment color
      fontWeight: '600',
    });
    this.tooltip.addChild(this.tooltipBg);
    this.tooltip.addChild(this.tooltipText);

    this.container.addChild(this.selectionRing);
    this.container.addChild(this.echoGlow);
    this.container.addChild(this.shape);
    this.container.addChild(this.hpBar);
    this.container.addChild(this.conditionIcons);
    this.container.addChild(this.deadOverlay);
    this.container.addChild(this.label);
    this.container.addChild(this.tooltip);

    // Initial render + snapshot tracking state
    this.render();
    this.prevHpTier = this.getHpTier();
    this.prevConditionKey = token.conditions.map(c => c.type).join(',');
    // Set up interaction
    this.setupInteraction();
  }

  /**
   * Update token data — only redraws changed parts for performance.
   * Full redraw on shape/size/visibility changes; incremental for HP/conditions/selection.
   */
  update(token: Token): void {
    const prev = this.data;
    this.data = token;

    // Check what actually changed
    const posChanged = prev.position.x !== token.position.x || prev.position.y !== token.position.y || prev.rotation !== token.rotation;
    const hpChanged = prev.currentHp !== token.currentHp || prev.maxHp !== token.maxHp;
    const condKey = token.conditions.map(c => c.type).join(',');
    const condChanged = condKey !== this.prevConditionKey;
    const selChanged = prev.selected !== token.selected;
    const visChanged = prev.visible !== token.visible || prev.visuals.color !== token.visuals.color ||
      prev.visuals.shape !== token.visuals.shape || prev.visuals.size !== token.visuals.size ||
      prev.tokenType !== token.tokenType || prev.locked !== token.locked ||
      prev.visuals.imageUrl !== token.visuals.imageUrl || prev.name !== token.name;

    // Full re-render only if visual fundamentals changed
    if (visChanged) {
      this.render();
      return;
    }

    // Otherwise, incremental updates
    const sizeInSquares = TOKEN_SIZE_SQUARES[token.visuals.size];
    const pixelSize = sizeInSquares * this.gridSize;
    const center = pixelSize / 2;

    if (posChanged) {
      this.container.x = token.position.x;
      this.container.y = token.position.y;
      this.container.rotation = (token.rotation * Math.PI) / 180;
    }

    if (selChanged) {
      this.renderSelectionRing(center, pixelSize);
    }

    if (hpChanged) {
      this.hpBar.clear();
      this.renderHpBar(pixelSize);
      // HP tier may affect border color on shape
      const newTier = this.getHpTier();
      if (newTier !== this.prevHpTier) {
        this.prevHpTier = newTier;
        this.renderShape(center, pixelSize);
        this.renderDeadOverlay(center, pixelSize);
      }
    }

    if (condChanged) {
      this.prevConditionKey = condKey;
      this.renderConditions(pixelSize);
    }
  }

  /**
   * Update grid size and re-render
   */
  setGridSize(size: number): void {
    this.gridSize = size;
    this.render();
  }

  /**
   * Render all token graphics
   */
  private render(): void {
    const { data, gridSize } = this;
    const sizeInSquares = TOKEN_SIZE_SQUARES[data.visuals.size];
    const pixelSize = sizeInSquares * gridSize;
    const center = pixelSize / 2;

    // Position container
    this.container.x = data.position.x;
    this.container.y = data.position.y;

    const isEcho = data.tokenType === 'echo';
    const isDead = this.getHpTier() === 'dead' && !isEcho;

    // Dead: tilt the token + desaturate via grayscale filter for a fallen look.
    // Echo & normal: standard rotation.
    if (isDead) {
      this.container.rotation = ((data.rotation + 12) * Math.PI) / 180;
      if (!this.container.filters || this.container.filters.length === 0) {
        const grayscale = new PIXI.ColorMatrixFilter();
        grayscale.desaturate();
        this.container.filters = [grayscale];
      }
    } else {
      this.container.rotation = (data.rotation * Math.PI) / 180;
      if (this.container.filters && this.container.filters.length > 0) {
        this.container.filters = null;
      }
    }

    this.container.alpha = isEcho ? 0.45
      : isDead ? 0.55
      : data.visible ? 1.0 : 0.4;
    this.container.cursor = data.locked ? 'not-allowed' : 'pointer';

    // Set hit area so container receives pointer events
    // Extend by 14px in all directions to cover selection ring glow + HP bar
    this.container.hitArea = new PIXI.Rectangle(-14, -14, pixelSize + 28, pixelSize + 28);

    // Draw selection ring
    this.renderSelectionRing(center, pixelSize);

    // Draw token shape or image
    this.renderShape(center, pixelSize);
    this.renderImage(center, pixelSize);

    // Draw echo glow aura (for Echo Knight echoes)
    this.renderEchoGlow(center, pixelSize);

    // Draw HP bar
    this.renderHpBar(pixelSize);

    // Draw label (only if no image)
    this.renderLabel(center, pixelSize);

    // Draw condition icons
    this.renderConditions(pixelSize);

    // Draw dead overlay (X mark)
    this.renderDeadOverlay(center, pixelSize);

    // Update tooltip position
    this.renderTooltip(center, pixelSize);
  }

  private renderSelectionRing(center: number, size: number): void {
    this.selectionRing.clear();

    if (!this.data.selected) return;

    const radius = size * 0.5;
    const glowColor = 0x00ffff; // Cyan glow

    // Draw outer glow layers (from outside in, increasing alpha)
    const glowLayers = [
      { offset: 12, alpha: 0.1, width: 8 },
      { offset: 8, alpha: 0.2, width: 6 },
      { offset: 5, alpha: 0.4, width: 4 },
    ];

    for (const layer of glowLayers) {
      this.selectionRing.lineStyle(layer.width, glowColor, layer.alpha);
      this.drawSelectionShape(center, size, radius, layer.offset);
    }

    // Draw solid inner ring
    this.selectionRing.lineStyle(2, glowColor, 1);
    this.drawSelectionShape(center, size, radius, 4);

    // Add slight glow filter for extra effect
    if (!this.selectionRing.filters || this.selectionRing.filters.length === 0) {
      // Use a simple alpha filter effect - the layered drawing provides the glow
    }
  }

  private drawSelectionShape(center: number, size: number, radius: number, offset: number): void {
    switch (this.data.visuals.shape) {
      case 'circle':
        this.selectionRing.drawCircle(center, center, radius + offset);
        break;
      case 'square':
        this.selectionRing.drawRoundedRect(
          -offset,
          -offset,
          size + offset * 2,
          size + offset * 2,
          4
        );
        break;
      case 'diamond':
        this.drawDiamond(this.selectionRing, center, center, radius + offset);
        break;
      case 'hex':
        this.drawHex(this.selectionRing, center, center, radius + offset);
        break;
    }
  }

  private renderShape(center: number, size: number): void {
    this.shape.clear();

    // Hide shape if we have a valid image
    if (this.imageSprite && this.imageSprite.visible) {
      this.shape.visible = false;
      return;
    }
    this.shape.visible = true;

    const isEcho = this.data.tokenType === 'echo';
    const color = isEcho ? 0x7dd3fc : parseInt(this.data.visuals.color.replace('#', ''), 16);
    const borderColor = isEcho ? 0x7dd3fc : this.getBorderColor();
    const borderWidth = this.getBorderWidth();
    const tier = this.getHpTier();

    // Apply desaturation for unconscious/dead (but not echoes — they just vanish)
    const alpha = isEcho ? 0.8 : (tier === 'unconscious' || tier === 'dead') ? 0.5 : 1;

    this.shape.lineStyle(borderWidth, borderColor, 1);
    this.shape.beginFill(color, alpha);

    const radius = size * 0.45;
    switch (this.data.visuals.shape) {
      case 'circle':
        this.shape.drawCircle(center, center, radius);
        break;
      case 'square':
        const margin = size * 0.05;
        this.shape.drawRect(margin, margin, size - margin * 2, size - margin * 2);
        break;
      case 'diamond':
        this.drawDiamond(this.shape, center, center, radius);
        break;
      case 'hex':
        this.drawHex(this.shape, center, center, radius);
        break;
    }

    this.shape.endFill();
  }

  /**
   * Render token image if imageUrl is provided
   */
  private renderImage(center: number, size: number): void {
    const imageUrl = this.data.visuals.imageUrl;

    // No image URL - hide sprite and show shape
    if (!imageUrl) {
      if (this.imageSprite) {
        this.imageSprite.visible = false;
      }
      this.shape.visible = true;
      return;
    }

    // Image URL changed - load new texture
    if (imageUrl !== this.currentImageUrl) {
      this.currentImageUrl = imageUrl;
      this.loadTokenImage(imageUrl, center, size);
      return;
    }

    // Update existing sprite position/size
    if (this.imageSprite && this.imageSprite.visible) {
      this.updateImageTransform(center, size);
    }
  }

  /**
   * Load image texture and create sprite
   */
  private async loadTokenImage(url: string, center: number, size: number): Promise<void> {
    try {
      // Load texture
      const texture = await PIXI.Assets.load<PIXI.Texture>(url);

      // Remove old sprite if exists and is actually a child of container
      if (this.imageSprite) {
        if (this.imageSprite.parent === this.container) {
          this.container.removeChild(this.imageSprite);
        }
        this.imageSprite.destroy();
        this.imageSprite = null;
      }
      if (this.imageMask) {
        if (this.imageMask.parent === this.container) {
          this.container.removeChild(this.imageMask);
        }
        this.imageMask.destroy();
        this.imageMask = null;
      }

      // Create sprite
      this.imageSprite = new PIXI.Sprite(texture);
      this.imageSprite.anchor.set(0.5);

      // Create circular mask
      this.imageMask = new PIXI.Graphics();
      const radius = size * 0.45;

      switch (this.data.visuals.shape) {
        case 'circle':
          this.imageMask.beginFill(0xffffff);
          this.imageMask.drawCircle(center, center, radius);
          this.imageMask.endFill();
          break;
        case 'square':
          const margin = size * 0.05;
          this.imageMask.beginFill(0xffffff);
          this.imageMask.drawRect(margin, margin, size - margin * 2, size - margin * 2);
          this.imageMask.endFill();
          break;
        default:
          // For diamond/hex, use circle mask for now
          this.imageMask.beginFill(0xffffff);
          this.imageMask.drawCircle(center, center, radius);
          this.imageMask.endFill();
      }

      // Apply mask
      this.imageSprite.mask = this.imageMask;

      // Add to container (after shape, before HP bar)
      // Use safe insertion - find shape index only if shape is a child
      if (this.shape.parent === this.container) {
        const shapeIndex = this.container.getChildIndex(this.shape);
        this.container.addChildAt(this.imageMask, shapeIndex + 1);
        this.container.addChildAt(this.imageSprite, shapeIndex + 2);
      } else {
        // Fallback: just add them (z-order may not be perfect but won't crash)
        this.container.addChild(this.imageMask);
        this.container.addChild(this.imageSprite);
      }

      // Position and scale
      this.updateImageTransform(center, size);

      // Hide shape + label, show image
      this.imageSprite.visible = true;
      this.shape.visible = false;
      this.label.visible = false;

      // Draw border around image
      this.drawImageBorder(center, size);

      // Cache successful image load for future sessions
      if (this.data.combatantId) {
        const baseName = this.data.name.replace(/\s+\d+$/, '').toLowerCase();
        useTokenImageStore.getState().setTokenImage(baseName, url);
      }

    } catch (err) {
      console.warn(`[TokenRenderer] Failed to load image: ${url}`, err);
      // Keep showing shape on error
      this.currentImageUrl = null;
      if (this.imageSprite) {
        this.imageSprite.visible = false;
      }
      this.shape.visible = true;
    }
  }

  /**
   * Update image sprite position and scale
   */
  private updateImageTransform(center: number, size: number): void {
    if (!this.imageSprite) return;

    const radius = size * 0.45;
    const diameter = radius * 2;

    // Position at center
    this.imageSprite.x = center;
    this.imageSprite.y = center;

    // Scale to fit
    const scale = diameter / Math.max(this.imageSprite.texture.width, this.imageSprite.texture.height);
    this.imageSprite.scale.set(scale);
  }

  /**
   * Draw border around token image
   */
  private drawImageBorder(center: number, size: number): void {
    const borderColor = this.getBorderColor();
    const borderWidth = this.getBorderWidth();
    const tier = this.getHpTier();
    const radius = size * 0.45;

    // Apply desaturation to image sprite for unconscious/dead
    if (this.imageSprite) {
      this.imageSprite.alpha = (tier === 'unconscious' || tier === 'dead') ? 0.5 : 1;
    }

    // Use shape graphics to draw border only
    this.shape.clear();
    this.shape.lineStyle(borderWidth, borderColor, 1);

    switch (this.data.visuals.shape) {
      case 'circle':
        this.shape.drawCircle(center, center, radius);
        break;
      case 'square':
        const margin = size * 0.05;
        this.shape.drawRect(margin, margin, size - margin * 2, size - margin * 2);
        break;
      case 'diamond':
        this.drawDiamond(this.shape, center, center, radius);
        break;
      case 'hex':
        this.drawHex(this.shape, center, center, radius);
        break;
    }

    this.shape.visible = true;
  }

  private renderHpBar(size: number): void {
    this.hpBar.clear();

    if (!this.data.visuals.showHpBar) return;
    if (this.data.currentHp === undefined || this.data.maxHp === undefined) return;
    if (this.data.maxHp <= 0) return;

    const barWidth = size * 0.8;
    const barHeight = 6;
    const barX = (size - barWidth) / 2;
    const barY = size + 4;

    const hpPercent = Math.max(0, Math.min(1, this.data.currentHp / this.data.maxHp));

    // Background
    this.hpBar.beginFill(0x1a1a1a);
    this.hpBar.drawRoundedRect(barX, barY, barWidth, barHeight, 2);
    this.hpBar.endFill();

    // HP fill
    const fillColor = this.getHpColor(hpPercent);
    this.hpBar.beginFill(fillColor);
    this.hpBar.drawRoundedRect(barX + 1, barY + 1, (barWidth - 2) * hpPercent, barHeight - 2, 1);
    this.hpBar.endFill();
  }

  private renderLabel(center: number, size: number): void {
    // Hide label if we have an image showing
    if (this.imageSprite && this.imageSprite.visible) {
      this.label.visible = false;
      return;
    }

    const labelText = this.data.visuals.label || this.getInitials(this.data.name);

    if (!labelText) {
      this.label.visible = false;
      return;
    }

    this.label.visible = true;
    this.label.text = labelText;
    this.label.anchor.set(0.5);
    this.label.x = center;
    this.label.y = center;

    // Scale label to fit
    const maxWidth = size * 0.7;
    const scale = Math.min(1, maxWidth / this.label.width);
    this.label.scale.set(scale);
  }

  /**
   * Render condition icons as small glyph badges below the token.
   * Each badge shows the condition glyph on a semi-transparent colored background.
   * Hover reveals the condition name.
   */
  private renderConditions(size: number): void {
    this.conditionIcons.removeChildren();

    if (!this.data.visuals.showConditionIcons) return;
    if (this.data.conditions.length === 0) return;

    const badgeSize = 14;
    const badgeGap = 2;
    const maxBadges = 6;
    const conditions = this.data.conditions.slice(0, maxBadges);
    const orient: 'above' | 'right' = 'above';

    // Position badges based on orientation
    const center = size / 2;
    const totalSpan = conditions.length * badgeSize + (conditions.length - 1) * badgeGap;

    conditions.forEach((condition, i) => {
      const color = this.getConditionColor(condition.type);
      const info = CONDITION_ICONS[condition.type] || { label: condition.type, path: '' };

      const badgeContainer = new PIXI.Container();
      if (orient === 'above') {
        badgeContainer.x = center - totalSpan / 2 + i * (badgeSize + badgeGap);
        badgeContainer.y = -(badgeSize + 4);
      } else {
        badgeContainer.x = size + 3;
        badgeContainer.y = i * (badgeSize + badgeGap);
      }
      badgeContainer.eventMode = 'static';
      badgeContainer.cursor = 'pointer';
      badgeContainer.hitArea = new PIXI.Rectangle(0, 0, badgeSize, badgeSize);

      // Background: rounded rect with condition color, semi-transparent
      const bg = new PIXI.Graphics();
      bg.beginFill(color, 0.55);
      bg.drawRoundedRect(0, 0, badgeSize, badgeSize, 3);
      bg.endFill();
      badgeContainer.addChild(bg);

      // Icon rendered as sprite from pre-rasterized texture (crisp at small sizes)
      if (info.path) {
        const texture = getConditionIconTexture(info.path);
        const icon = new PIXI.Sprite(texture);
        const iconPad = 2;
        const iconSize = badgeSize - iconPad * 2;
        icon.x = iconPad;
        icon.y = iconPad;
        icon.width = iconSize;
        icon.height = iconSize;
        badgeContainer.addChild(icon);
      }

      // Tooltip on hover
      const tipText = new PIXI.Text(info.label, {
        fontFamily: 'Georgia, serif',
        fontSize: 10,
        fontWeight: 'bold',
        fill: 0xf4e4bc,
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowBlur: 2,
        dropShadowDistance: 0,
      });
      if (orient === 'above') {
        tipText.anchor.set(0.5, 1);
        tipText.x = badgeSize / 2;
        tipText.y = -4;
      } else {
        tipText.anchor.set(0, 0.5);
        tipText.x = badgeSize + 4;
        tipText.y = badgeSize / 2;
      }
      tipText.visible = false;
      badgeContainer.addChild(tipText);

      badgeContainer.on('pointerover', (e: PIXI.FederatedPointerEvent) => {
        // Prevent the parent token tooltip (character name) from firing
        e.stopPropagation();
        this.tooltip.visible = false;
        tipText.visible = true;
        bg.clear();
        bg.beginFill(color, 0.85);
        bg.drawRoundedRect(0, 0, badgeSize, badgeSize, 3);
        bg.endFill();
      });
      badgeContainer.on('pointerout', (e: PIXI.FederatedPointerEvent) => {
        e.stopPropagation();
        tipText.visible = false;
        bg.clear();
        bg.beginFill(color, 0.55);
        bg.drawRoundedRect(0, 0, badgeSize, badgeSize, 3);
        bg.endFill();
      });

      this.conditionIcons.addChild(badgeContainer);
    });

    // Overflow indicator
    if (this.data.conditions.length > maxBadges) {
      const moreText = new PIXI.Text(`+${this.data.conditions.length - maxBadges}`, {
        fontFamily: 'Georgia, serif',
        fontSize: 9,
        fill: 0xcccccc,
        fontWeight: 'bold',
      });
      if (orient === 'above') {
        moreText.anchor.set(0, 0.5);
        moreText.x = center + totalSpan / 2 + 3;
        moreText.y = -(badgeSize + 4) + badgeSize / 2;
      } else {
        moreText.anchor.set(0.5, 0);
        moreText.x = size + 3 + badgeSize / 2;
        moreText.y = conditions.length * (badgeSize + badgeGap);
      }
      this.conditionIcons.addChild(moreText);
    }
  }

  /**
   * Render and position the tooltip below the token.
   * Conditions render above the token — putting the name tooltip below
   * avoids overlap and keeps the name out of the condition badge area.
   */
  private renderTooltip(center: number, size: number): void {
    this.tooltipText.text = this.data.name;

    const padding = 6;
    const textWidth = this.tooltipText.width;
    const textHeight = this.tooltipText.height;

    // Draw background pill
    this.tooltipBg.clear();
    this.tooltipBg.beginFill(0x1a1410, 0.9);
    this.tooltipBg.lineStyle(1, 0xd4af37, 0.8);
    this.tooltipBg.drawRoundedRect(0, 0, textWidth + padding * 2, textHeight + padding, 4);
    this.tooltipBg.endFill();

    // Text centered in background
    this.tooltipText.x = padding;
    this.tooltipText.y = padding / 2;

    // Center tooltip below token (leave space for HP bar + label)
    this.tooltip.x = center - (textWidth + padding * 2) / 2;
    this.tooltip.y = size + 18;
  }

  /**
   * Render dead overlay — spectral skull silhouette with fading ring
   * More evocative than a simple red X
   */
  private renderDeadOverlay(center: number, size: number): void {
    this.deadOverlay.clear();

    // Echo tokens don't show dead overlay — they just get removed
    if (this.data.tokenType === 'echo') return;

    const tier = this.getHpTier();
    if (tier !== 'dead') return;

    const r = size * 0.35;

    // Outer fading ring — dark crimson, ghostly
    this.deadOverlay.lineStyle(2, 0x3d0000, 0.5);
    this.deadOverlay.drawCircle(center, center, r + 6);
    this.deadOverlay.lineStyle(1, 0x1a0000, 0.3);
    this.deadOverlay.drawCircle(center, center, r + 10);

    // Inner dark vignette circle
    this.deadOverlay.beginFill(0x000000, 0.45);
    this.deadOverlay.drawCircle(center, center, r);
    this.deadOverlay.endFill();

    // Skull silhouette — simple geometric skull shape
    const s = r * 0.55; // skull scale
    const cx = center;
    const cy = center - s * 0.1;

    // Cranium (rounded top)
    this.deadOverlay.beginFill(0x8b0000, 0.7);
    this.deadOverlay.drawEllipse(cx, cy - s * 0.15, s * 0.7, s * 0.65);
    this.deadOverlay.endFill();

    // Jaw (narrower bottom)
    this.deadOverlay.beginFill(0x8b0000, 0.7);
    this.deadOverlay.drawEllipse(cx, cy + s * 0.45, s * 0.45, s * 0.3);
    this.deadOverlay.endFill();

    // Eye sockets (dark holes)
    this.deadOverlay.beginFill(0x000000, 0.9);
    this.deadOverlay.drawEllipse(cx - s * 0.25, cy - s * 0.1, s * 0.18, s * 0.22);
    this.deadOverlay.drawEllipse(cx + s * 0.25, cy - s * 0.1, s * 0.18, s * 0.22);
    this.deadOverlay.endFill();

    // Nose cavity (inverted triangle)
    this.deadOverlay.beginFill(0x000000, 0.8);
    this.deadOverlay.moveTo(cx, cy + s * 0.05);
    this.deadOverlay.lineTo(cx - s * 0.08, cy + s * 0.2);
    this.deadOverlay.lineTo(cx + s * 0.08, cy + s * 0.2);
    this.deadOverlay.closePath();
    this.deadOverlay.endFill();
  }

  /**
   * Render a pulsing cyan glow around echo tokens
   */
  private renderEchoGlow(center: number, size: number): void {
    this.echoGlow.clear();

    if (this.data.tokenType !== 'echo') {
      // Clean up ticker if token is no longer an echo
      if (this.echoTickerFn) {
        PIXI.Ticker.shared.remove(this.echoTickerFn);
        this.echoTickerFn = null;
      }
      return;
    }

    const radius = size * 0.5;
    const pulseAlpha = 0.25 + Math.sin(this.echoPhase) * 0.15; // Pulses 0.1 — 0.4

    // Outer glow rings
    this.echoGlow.lineStyle(6, 0x7dd3fc, pulseAlpha * 0.4);
    this.echoGlow.drawCircle(center, center, radius + 8);
    this.echoGlow.lineStyle(4, 0x7dd3fc, pulseAlpha * 0.7);
    this.echoGlow.drawCircle(center, center, radius + 4);
    this.echoGlow.lineStyle(2, 0x7dd3fc, pulseAlpha);
    this.echoGlow.drawCircle(center, center, radius + 1);

    // Start animation ticker if not already running
    if (!this.echoTickerFn) {
      this.echoTickerFn = () => {
        this.echoPhase += 0.04;
        if (this.data.tokenType === 'echo') {
          const sizeInSquares = TOKEN_SIZE_SQUARES[this.data.visuals.size];
          const px = sizeInSquares * this.gridSize;
          const c = px / 2;
          this.renderEchoGlow(c, px);
        }
      };
      PIXI.Ticker.shared.add(this.echoTickerFn);
    }
  }

  private setupInteraction(): void {
    // Show tooltip on hover — but if GM has always-on mode, it stays visible.
    this.container.on('pointerover', () => {
      if (!this.isDragging) {
        this.tooltip.visible = true;
      }
    });

    this.container.on('pointerout', () => {
      this.tooltip.visible = TokenRenderer.alwaysShowNames;
    });

    this.container.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      if (this.data.locked) return;

      event.stopPropagation();
      setPixiTokenClicked(true);  // Tell React not to clear selection
      this.isDragging = true;
      this.hasMoved = false;  // Reset movement tracking
      this.tooltip.visible = false;  // Hide tooltip when dragging

      const pos = event.getLocalPosition(this.container.parent);
      this.dragOffset.x = pos.x - this.container.x;
      this.dragOffset.y = pos.y - this.container.y;
      this.dragStart.x = this.container.x;
      this.dragStart.y = this.container.y;

      this.onDragStart?.(this.tokenId);
    });

    this.container.on('pointerup', (event: PIXI.FederatedPointerEvent) => {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.container.alpha = this.data.tokenType === 'echo' ? 0.45 : this.data.visible ? 1.0 : 0.4;

      if (!this.hasMoved) {
        // No movement = this was a click, select the token
        this.onClick?.(this.tokenId, event.shiftKey);
      } else {
        // Clamp to valid bounds on drop so token is always reachable
        this.clampToMapBounds();
        this.onDragEnd?.(this.tokenId, this.container.x, this.container.y);
      }
    });

    this.container.on('pointerupoutside', () => {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.container.alpha = this.data.tokenType === 'echo' ? 0.45 : this.data.visible ? 1.0 : 0.4;

      if (this.hasMoved) {
        // Clamp to valid bounds on drop so token is always reachable
        this.clampToMapBounds();
        this.onDragEnd?.(this.tokenId, this.container.x, this.container.y);
      }
    });

    this.container.on('globalpointermove', (event: PIXI.FederatedPointerEvent) => {
      if (!this.isDragging) return;

      const pos = event.getLocalPosition(this.container.parent);
      const newX = pos.x - this.dragOffset.x;
      const newY = pos.y - this.dragOffset.y;

      // Check if we've moved beyond a small threshold (5px)
      const dx = Math.abs(newX - this.dragStart.x);
      const dy = Math.abs(newY - this.dragStart.y);
      if (dx > 5 || dy > 5) {
        this.hasMoved = true;
        this.container.alpha = 0.7;  // Only dim when actually dragging
      }

      if (this.hasMoved) {
        // No clamping during drag — let token follow cursor freely.
        // Bounds enforced on drop (pointerup) so token is always reachable.
        this.container.x = newX;
        this.container.y = newY;
        this.onDragMove?.(this.tokenId, this.container.x, this.container.y);
      }
    });
  }

  /** Snap token position back into valid map bounds (called on drop). */
  private clampToMapBounds(): void {
    const maxPx = 200 * this.gridSize;
    this.container.x = Math.max(0, Math.min(this.container.x, maxPx));
    this.container.y = Math.max(0, Math.min(this.container.y, maxPx));
  }

  // Helper methods
  private drawDiamond(g: PIXI.Graphics, cx: number, cy: number, r: number): void {
    g.moveTo(cx, cy - r);
    g.lineTo(cx + r, cy);
    g.lineTo(cx, cy + r);
    g.lineTo(cx - r, cy);
    g.closePath();
  }

  private drawHex(g: PIXI.Graphics, cx: number, cy: number, r: number): void {
    const points: number[] = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i * 60 - 90) * (Math.PI / 180);
      points.push(cx + r * Math.cos(angle), cy + r * Math.sin(angle));
    }
    g.drawPolygon(points);
  }

  private getHpColor(percent: number): number {
    if (percent > 0.5) return 0x5cb85c; // Green
    if (percent > 0.25) return 0xf0ad4e; // Orange
    return 0xd9534f; // Red
  }

  private getConditionColor(type: string): number {
    const colors: Record<string, number> = {
      blinded: 0x4a4a4a,
      charmed: 0xff69b4,
      deafened: 0x808080,
      frightened: 0x9932cc,
      grappled: 0x8b4513,
      incapacitated: 0x696969,
      invisible: 0x00ffff,
      paralyzed: 0xffd700,
      petrified: 0xa0a0a0,
      poisoned: 0x32cd32,
      prone: 0x8b0000,
      restrained: 0xcd853f,
      stunned: 0xffff00,
      unconscious: 0x000080,
      exhaustion: 0x4b0082,
      concentration: 0x00bfff,
      bloodied: 0xdc2626,
    };
    return colors[type] || 0xffffff;
  }

  private getInitials(name: string): string {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  /**
   * Get HP tier from token data (or calculate from HP)
   */
  private getHpTier(): HpTier | null {
    // Use stored tier if available
    if (this.data.hpTier) return this.data.hpTier;

    // Calculate from HP if available
    if (this.data.currentHp !== undefined && this.data.maxHp && this.data.maxHp > 0) {
      const percent = (this.data.currentHp / this.data.maxHp) * 100;
      if (this.data.currentHp <= 0) return 'unconscious';
      if (percent >= 75) return 'healthy';
      if (percent >= 50) return 'wounded';
      if (percent >= 25) return 'bloody';
      return 'critical';
    }

    return null;
  }

  /**
   * Get border color based on HP tier (or default)
   */
  private getBorderColor(): number {
    const tier = this.getHpTier();

    // Use HP tier color if not healthy (healthy uses default)
    if (tier && tier !== 'healthy') {
      return HP_TIER_COLORS[tier];
    }

    // Default border color
    return this.data.visuals.borderColor
      ? parseInt(this.data.visuals.borderColor.replace('#', ''), 16)
      : 0x000000;
  }

  /**
   * Get border width based on HP tier
   */
  private getBorderWidth(): number {
    const tier = this.getHpTier();
    // Thicker border for critical/unconscious
    if (tier === 'critical' || tier === 'unconscious' || tier === 'dead') {
      return 3;
    }
    return 2;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    TokenRenderer.instances.delete(this);
    // Clean up echo animation ticker
    if (this.echoTickerFn) {
      PIXI.Ticker.shared.remove(this.echoTickerFn);
      this.echoTickerFn = null;
    }

    // Clean up image resources
    if (this.imageSprite) {
      this.imageSprite.destroy();
      this.imageSprite = null;
    }
    if (this.imageMask) {
      this.imageMask.destroy();
      this.imageMask = null;
    }
    this.currentImageUrl = null;

    this.container.destroy({ children: true });
  }
}
